import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { getNavigationPermission } from 'shared';
import { setupPermissionGuard } from './permission-guard';
import { authApi } from '@/api/auth.api';
import { buildWechatAuthorizeUrl, canAttemptWechatOAuth } from '@/utils/wechat-auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login/Login.vue'),
    meta: { layout: 'auth', requiresAuth: false },
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: () => import('@/views/auth/ChangePassword.vue'),
    meta: { layout: 'auth', requiresAuth: true },
  },
  {
    path: '/wechat-auth',
    name: 'wechat-auth',
    component: () => import('@/views/auth/WechatAuth.vue'),
    meta: { layout: 'auth', requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('@/components/layout/MainLayout.vue'),
    redirect: '/dashboard',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/dashboard/Dashboard.vue'),
        meta: { title: '数据看板', permission: getNavigationPermission('stats') },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/auth/ProfilePage.vue'),
        meta: { title: '个人信息' },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/settings/SettingsPage.vue'),
        meta: {
          title: '系统设置',
          permission: getNavigationPermission('settings'),
        },
      },
      {
        path: 'settings/platform-access',
        name: 'platform-access-settings',
        component: () => import('@/views/settings/PlatformAccessSettings.vue'),
        meta: { title: '平台接入设置', permission: 'platform:view' },
      },
      {
        path: 'settings/custom-fields',
        name: 'custom-field-list',
        component: () => import('@/views/settings/CustomFieldList.vue'),
        meta: { title: '客资字段设置', permission: 'custom-field:view' },
      },
      {
        path: 'settings/crm-rules',
        name: 'crm-settings',
        component: () => import('@/views/settings/CrmSettings.vue'),
        meta: { title: '客资规则', permission: 'lead:settings' },
      },
      {
        path: 'settings/wechat-official',
        name: 'wechat-official-settings',
        component: () => import('@/views/settings/WechatOfficialSettings.vue'),
        meta: { title: '微信服务号接入', permission: 'wechat-official:view' },
      },
      {
        path: 'org',
        name: 'org-management',
        component: () => import('@/views/org/OrgManagement.vue'),
        meta: { title: '组织管理', permission: getNavigationPermission('dealer') },
      },
      {
        path: 'org/members',
        name: 'member-management',
        component: () => import('@/views/org/MemberManagement.vue'),
        meta: { title: '成员管理', permission: getNavigationPermission('member-mgmt') },
      },
      {
        path: 'platforms/:platformCode/accounts',
        name: 'platform-accounts',
        component: () => import('@/views/platform/AccountList.vue'),
        meta: { title: '账号管理', permission: 'platform:view' },
      },
      {
        path: 'platforms/:platformCode/accounts/:accountId/monitor',
        name: 'pull-monitor',
        component: () => import('@/views/platform/PullMonitor.vue'),
        meta: { title: '拉取监控', permission: 'platform:view' },
      },
      {
        path: 'crm',
        name: 'crm-record-list',
        component: () => import('@/views/crm/CrmRecordList.vue'),
        meta: { title: '客资', permission: getNavigationPermission('crm-record') },
      },
      {
        path: 'crm/recycle-bin',
        name: 'crm-recycle-bin',
        component: () => import('@/views/crm/CrmRecycleBin.vue'),
        meta: { title: '客资回收站', permission: getNavigationPermission('crm-recycle-bin') },
      },
      {
        path: 'crm/:id',
        name: 'crm-record-detail',
        component: () => import('@/views/crm/CrmRecordDetail.vue'),
        meta: { title: '客资详情', permission: 'lead:view' },
      },
      {
        path: 'imports',
        name: 'import-history',
        component: () => import('@/views/import/ImportHistory.vue'),
        meta: { title: '导入历史', permission: ['lead:import', 'dealer:import', 'department:import'] },
      },
      {
        path: 'imports/wizard',
        name: 'import-wizard',
        component: () => import('@/views/import/ImportWizard.vue'),
        meta: { title: '新建导入', permission: ['lead:import', 'dealer:import', 'department:import'] },
      },
      {
        path: 'platform-logs/webhook',
        name: 'webhook-push-log-list',
        component: () => import('@/views/platform/WebhookPushLogList.vue'),
        meta: { title: '推送日志', permission: getNavigationPermission('webhook-push-logs') },
      },
      {
        path: 'platform-logs/raw-leads',
        name: 'raw-lead-list',
        component: () => import('@/views/platform/RawLeadList.vue'),
        meta: { title: '原始客资', permission: getNavigationPermission('raw-lead-list') },
      },
      {
        path: 'notifications',
        name: 'notification-center',
        component: () => import('@/views/notification/NotificationCenter.vue'),
        meta: { title: '通知中心' },
      },
      // RBAC 角色管理
      {
        path: 'roles',
        name: 'role-list',
        component: () => import('@/views/role/RoleList.vue'),
        meta: { title: '角色管理', permission: getNavigationPermission('role'), superOnly: true },
      },
      {
        path: 'roles/create',
        name: 'role-create',
        component: () => import('@/views/role/RoleForm.vue'),
        meta: { title: '新建角色', permission: 'role:create', superOnly: true },
      },
      {
        path: 'roles/:id/edit',
        name: 'role-edit',
        component: () => import('@/views/role/RoleForm.vue'),
        meta: { title: '编辑角色', permission: 'role:edit', superOnly: true },
      },
    ],
  },
  // 暂时隐藏企微侧边栏入口，页面实现继续保留。
  // {
  //   path: '/sidebar',
  //   component: () => import('@/views/sidebar/SidebarLayout.vue'),
  //   meta: { requiresAuth: false },
  //   children: [
  //     {
  //       path: '',
  //       name: 'sidebar-index',
  //       component: () => import('@/views/sidebar/SidebarIndex.vue'),
  //       meta: { title: '侧边栏' },
  //     },
  //   ],
  // },
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/views/errors/403.vue'),
    meta: { layout: 'auth', requiresAuth: false },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/error/NotFound.vue'),
    meta: { layout: 'auth' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * 全局路由守卫（骨架版）
 *
 * - 未登录访问需鉴权页面 → 跳转 /login
 * - 已登录但 mustChangePassword=true 强制跳 /change-password
 *
 * 完整版（菜单 / 权限校验）在 2.1.7 中加强
 */
router.beforeEach(async (to) => {
  const { useAuthStore } = await import('@/stores/auth');
  const auth = useAuthStore();
  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    if (canAttemptWechatOAuth()) {
      try {
        const availability = await authApi.wechatAvailability();
        if (availability.enabled) {
          window.location.assign(buildWechatAuthorizeUrl(to.fullPath));
          return false;
        }
      } catch {
        // 微信登录不可用时回退账号密码登录。
      }
    }
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (auth.isLoggedIn && auth.mustChangePassword && to.name !== 'change-password') {
    return { name: 'change-password' };
  }
  // 登录后已缓存 profile；仅页面刷新恢复会话时重新拉取一次。
  if (auth.isLoggedIn && !auth.profileLoaded) {
    try {
      await auth.fetchProfile();
    } catch {
      auth.clearSession();
      return { name: 'login', query: { redirect: to.fullPath } };
    }
  }
  return true;
});

// 设置 RBAC 路由权限守卫
setupPermissionGuard(router);

export default router;
